import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import './AdminTuneMetadata.css';
import AssetSearchModal from './AssetSearchModal';
import ProductSearchModal from './ProductSearchModal';
import TuneFieldSelectWithAdd from './TuneFieldSelectWithAdd';
import {
  DEFAULT_TUNE_METADATA_FIELD_OPTIONS,
} from '../constants/tuneMetadataFieldOptions';
import { fetchTuneSiteOptions } from '../utils/tuneSiteOptions';
import { normalizeTimeSignature } from '../constants/tuneTimeSignatures';
import { getProductImageUrlSync } from '../utils/assetImageMapping';

// ── Dropdown field presets ────────────────────────────────────────────────────

const DROPDOWN_FIELDS = new Set(['region', 'tune_type', 'key_signature', 'time_signature']);

// ── Tag taxonomy & editor ─────────────────────────────────────────────────────

const TAG_TAXONOMY = {
  'Content':    ['tune', 'notation', 'sheet-music', 'tune-notation', 'cd', 'composer'],
  'Region':     ['cape-breton', 'scottish', 'irish', 'celtic', 'celtic-colours',
                  'nova-scotian', 'shetland', 'glengarry', 'branches', 'psc',
                  'scottish-traditional', 'irish-traditional'],
  'Tune Type':  ['reel', 'jig', 'strathspey', 'march', 'air', 'slow-air',
                  'hornpipe', 'waltz', 'polka', 'slide', 'slow', 'medium-tempo'],
  'Key':        ['a-major', 'a-minor', 'b-minor', 'd-major', 'e-minor',
                  'g-major', 'f-major', 'c-major', 'g-minor', 'd-minor', 'f-sharp-minor'],
  'Collection': ['the-beaton-family-of-mabou', 'celtic-colours-collection',
                  'the-cape-breton-highland-collection', 'the-lighthouse-collection',
                  'the-simon-fraser-collection', 'cape-breton-scottish-collection',
                  'the-fiddletree', 'in-jig-time'],
};

const TAG_CAT_COLORS = {
  'Content':    '#6366f1',
  'Region':     '#0891b2',
  'Tune Type':  '#d97706',
  'Key':        '#7c3aed',
  'Collection': '#059669',
};

function getTagColor(tag) {
  for (const [cat, list] of Object.entries(TAG_TAXONOMY)) {
    if (list.includes(tag)) return TAG_CAT_COLORS[cat] || '#64748b';
  }
  return '#64748b'; // custom / unknown tag
}

function parseTags(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val.map(t => String(t).trim()).filter(Boolean);
  return String(val).split(/[,;|]+/).map(t => t.trim()).filter(Boolean);
}

const TUNE_META_API_BASE =
  typeof process !== 'undefined' && process.env.REACT_APP_API_URL
    ? String(process.env.REACT_APP_API_URL).replace(/\/$/, '')
    : '';

function tuneMetaApiUrl(path) {
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${TUNE_META_API_BASE}${p}`;
}

/** When the server returns HTML (404 page, proxy error), avoid JSON.parse throwing "Unexpected token '<'". */
async function tuneMetaParseApiJson(resp) {
  const text = await resp.text();
  const ct = resp.headers.get('content-type') || '';
  if (ct.includes('application/json') || text.trimStart().startsWith('{') || text.trimStart().startsWith('[')) {
    try {
      return JSON.parse(text);
    } catch (_) {
      throw new Error(`Invalid JSON (HTTP ${resp.status})`);
    }
  }
  if (text.trimStart().startsWith('<')) {
    const hint =
      resp.status === 404
        ? 'API returned HTML (HTTP 404). Restart the backend after updating code, or set REACT_APP_API_URL if the dev proxy does not reach your API.'
        : 'Server returned HTML instead of JSON.';
    throw new Error(hint);
  }
  throw new Error(text.slice(0, 200).trim() || `HTTP ${resp.status}`);
}

/** Preview / result breakdown for POST /sync/to-content-pages */
function ToContentPagesApplyBreakdown({ payload, variant }) {
  if (!payload || payload.direction !== 'to-content-pages') return null;
  const s = payload.samples || {};
  const lim = payload.sampleLimit || 100;
  const isPreview = variant === 'preview';
  const isCreateMissing = payload.mode === 'create_missing';
  const updatedCount = isPreview ? (payload.wouldUpdate ?? 0) : (payload.updated ?? payload.contentPagesUpdated ?? 0);
  const createdCount = isPreview ? (payload.wouldCreate ?? 0) : (payload.created ?? payload.contentPagesCreated ?? 0);
  const mainLabel = isCreateMissing
    ? (isPreview ? 'Will create (new pages)' : 'Created (new pages)')
    : (isPreview ? 'Will update' : 'Updated');
  const mainList = isCreateMissing
    ? (isPreview ? s.wouldCreate : s.created)
    : (isPreview ? s.wouldUpdate : s.applied);
  const createList = isPreview ? s.wouldCreate : s.created;

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 12,
    background: 'var(--color-surface)',
  };
  const th = { textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' };
  const td = { padding: '6px 8px', borderBottom: '1px solid var(--color-border)', verticalAlign: 'top' };

  const SampleTable = ({ title, rows, cols }) => {
    if (!rows || rows.length === 0) return null;
    return (
      <details style={{ marginTop: 10 }}>
        <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
          {title}
          <span style={{ fontWeight: 400, color: 'var(--color-text-secondary)', marginLeft: 6 }}>
            (sample {rows.length}{rows.length >= lim ? ` · max ${lim} shown` : ''})
          </span>
        </summary>
        <div style={{
          maxHeight: 240, overflow: 'auto', marginTop: 8,
          border: '1px solid var(--color-border)', borderRadius: 8,
        }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                {cols.map((c) => <th key={c.key} style={th}>{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={`${row.page_id}-${i}`}>
                  {cols.map((c) => <td key={c.key} style={td}>{row[c.key] != null ? String(row[c.key]) : '—'}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    );
  };

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '8px 14px', fontSize: 13, marginBottom: 4,
        padding: '10px 12px', borderRadius: 8,
        background: 'var(--color-surface-hover)',
      }}>
        {isCreateMissing ? (
          <span><strong style={{ color: 'var(--color-success)' }}>{createdCount}</strong> {mainLabel.toLowerCase()}</span>
        ) : (
          <>
            <span><strong style={{ color: 'var(--color-success)' }}>{updatedCount}</strong>{' '}{isPreview ? 'will update' : 'updated'}</span>
            <span><strong style={{ color: 'var(--color-success)' }}>{createdCount}</strong>{' '}{isPreview ? 'will create (new page_id)' : 'created (new page_id)'}</span>
          </>
        )}
        {!isCreateMissing && (
          <span><strong>{payload.skippedUnchanged ?? 0}</strong> unchanged (skipped)</span>
        )}
        {isCreateMissing && (
          <span><strong>{payload.skippedAlreadyExists ?? 0}</strong> already on site (skipped)</span>
        )}
        {(payload.skippedMissingPage ?? 0) > 0 && (
          <span><strong>{payload.skippedMissingPage}</strong> no matching page (skipped)</span>
        )}
        <span><strong>{payload.skippedWrongType ?? 0}</strong> wrong page type</span>
        <span><strong>{payload.skippedNoPageId ?? 0}</strong> no page_id in row</span>
        <span style={{ color: 'var(--color-text-secondary)' }}>
          Staging rows: <strong>{payload.stagingRows ?? 0}</strong>
        </span>
      </div>
      <SampleTable
        title={mainLabel}
        rows={mainList}
        cols={[{ key: 'page_id', label: 'page_id' }, { key: 'title', label: 'Title' }]}
      />
      {!isCreateMissing && (
        <SampleTable
          title={isPreview ? 'New tune pages (staging page_id had no live row)' : 'New tune pages created'}
          rows={createList}
          cols={[{ key: 'page_id', label: 'page_id' }, { key: 'title', label: 'Title' }]}
        />
      )}
      {!isCreateMissing && (
        <SampleTable
          title="Unchanged (no DB write)"
          rows={s.skippedUnchanged}
          cols={[{ key: 'page_id', label: 'page_id' }, { key: 'title', label: 'Title' }]}
        />
      )}
      {(payload.skippedMissingPage ?? 0) > 0 && (
        <SampleTable
          title="No matching content page (skipped — not overwrite/add_new)"
          rows={s.skippedMissing}
          cols={[{ key: 'page_id', label: 'page_id' }, { key: 'title', label: 'Title' }]}
        />
      )}
      {isCreateMissing && (
        <SampleTable
          title="Already exists (tune / ABC — not modified)"
          rows={s.skippedAlreadyExists}
          cols={[{ key: 'page_id', label: 'page_id' }, { key: 'title', label: 'Title' }]}
        />
      )}
      <SampleTable
        title="Wrong page type (not tune / ABC)"
        rows={s.skippedWrongType}
        cols={[{ key: 'page_id', label: 'page_id' }, { key: 'title', label: 'Title' }, { key: 'page_type', label: 'page_type' }]}
      />
      <p style={{ margin: '14px 0 0', fontSize: 12, color: 'var(--color-text-secondary)', lineHeight: 1.45 }}>
        {isCreateMissing ? (
          <>
            Inserts new <code>content_pages</code> rows for staging <code>page_id</code>s that are not in the database yet.
            Default type is <code>tune-notation</code>; set <code>page_type</code> (or <code>pageType</code>) in <strong>extra</strong> columns to <code>abc-notation</code> if needed.
            Existing tune pages are left unchanged.
          </>
        ) : (
          <>
            <strong>Overwrite</strong> and <strong>Add only</strong> update existing tune / ABC pages. If a staging <code>page_id</code> has no live row yet (e.g. you renamed <code>page_id</code>), a <strong>new</strong> tune page is created automatically.
            Use <strong>Create missing only</strong> when you want inserts only and must not change existing pages.
          </>
        )}
      </p>
    </div>
  );
}

function TagEditor({ value, onSave, compact = false }) {
  const [tags, setTags]           = useState(() => parseTags(value));
  const [showPalette, setShowPalette] = useState(false);
  const [inputVal, setInputVal]   = useState('');
  const inputRef  = useRef(null);
  const wrapRef   = useRef(null);

  // Sync when external value changes (e.g. after research apply)
  useEffect(() => { setTags(parseTags(value)); }, [value]);

  // Close palette when clicking outside
  useEffect(() => {
    if (!showPalette) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowPalette(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showPalette]);

  const commit = (newTags) => {
    setTags(newTags);
    onSave(newTags.join(','));
  };

  const addTag = (tag) => {
    const t = tag.trim().toLowerCase().replace(/\s+/g, '-');
    if (!t || tags.includes(t)) return;
    commit([...tags, t]);
  };

  const removeTag = (tag) => commit(tags.filter(t => t !== tag));

  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && inputVal.trim()) {
      e.preventDefault();
      addTag(inputVal);
      setInputVal('');
    } else if (e.key === 'Backspace' && !inputVal && tags.length) {
      removeTag(tags[tags.length - 1]);
    } else if (e.key === 'Escape') {
      setShowPalette(false);
      inputRef.current?.blur();
    }
  };

  const chipStyle = (tag) => ({
    display: 'inline-flex', alignItems: 'center', gap: 3,
    padding: compact ? '1px 6px' : '2px 9px',
    borderRadius: 12,
    background: `${getTagColor(tag)}1a`,
    border: `1px solid ${getTagColor(tag)}55`,
    color: getTagColor(tag),
    fontSize: compact ? 10 : 11,
    fontWeight: 600, whiteSpace: 'nowrap', lineHeight: 1.4,
  });

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      {/* Tag chips + text input row */}
      <div
        style={{
          display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center',
          padding: '4px 6px', minHeight: compact ? 28 : 34,
          border: '1px solid var(--color-border)',
          borderRadius: 6, cursor: 'text', boxSizing: 'border-box',
        }}
        onClick={() => inputRef.current?.focus()}
      >
        {tags.map(tag => (
          <span key={tag} style={chipStyle(tag)}>
            {tag}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeTag(tag); }}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: 0, lineHeight: 1, color: 'inherit',
                fontSize: compact ? 10 : 12, marginLeft: 2,
              }}
              title={`Remove "${tag}"`}
            >×</button>
          </span>
        ))}
        <input
          ref={inputRef}
          value={inputVal}
          onChange={e => setInputVal(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowPalette(true)}
          placeholder={tags.length === 0 ? 'Add tags…' : ''}
          style={{
            border: 'none', outline: 'none', background: 'transparent',
            color: 'inherit', fontSize: 12, minWidth: 80, flexGrow: 1,
          }}
        />
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); setShowPalette(p => !p); }}
          title="Browse tag palette"
          style={{
            border: 'none', background: 'none', cursor: 'pointer',
            fontSize: 15, padding: '0 2px', lineHeight: 1,
            color: 'var(--color-text-secondary)',
          }}
        >⊕</button>
      </div>

      {/* Tag palette dropdown */}
      {showPalette && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 1200,
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 12, padding: '12px 14px', width: 360,
          boxShadow: '0 8px 30px rgba(0,0,0,.18)',
          maxHeight: 340, overflowY: 'auto',
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-secondary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '.05em' }}>
            Tag palette — click to toggle · type + Enter to add custom
          </div>
          {Object.entries(TAG_TAXONOMY).map(([cat, catTags]) => (
            <div key={cat} style={{ marginBottom: 10 }}>
              <div style={{
                fontSize: 10, fontWeight: 800, textTransform: 'uppercase',
                letterSpacing: '.07em', color: TAG_CAT_COLORS[cat],
                marginBottom: 5,
              }}>{cat}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {catTags.map(tag => {
                  const active = tags.includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); active ? removeTag(tag) : addTag(tag); }}
                      style={{
                        padding: '2px 10px', borderRadius: 10,
                        fontSize: 11, cursor: 'pointer', fontWeight: 600,
                        background: active ? `${TAG_CAT_COLORS[cat]}22` : 'transparent',
                        border: `1px solid ${active ? TAG_CAT_COLORS[cat] : TAG_CAT_COLORS[cat] + '55'}`,
                        color: active ? TAG_CAT_COLORS[cat] : 'var(--color-text-secondary)',
                        transition: 'all .12s',
                      }}
                    >
                      {active ? '✓ ' : ''}{tag}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// toCSV kept for potential local fallback use
function toCSV(headers, rows) {
  const esc = (v) => {
    const s = String(v ?? '').replace(/\r?\n/g, ' ');
    return /[",]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [headers.join(','), ...rows.map(r => headers.map(h => esc(r[h])).join(','))].join('\n');
}

function isMissing(val) {
  const v = String(val || '').trim().toLowerCase();
  return !v || v === 'unknown' || v === 'not mentioned' || v === 'none' || v === 'n/a' || v === '-';
}

/** Text search — skips fields with an active "Missing …" filter (those cells are empty on every visible row). */
function rowMatchesSearchTerm(row, term, missingFilters, customMissingField) {
  if (!term) return true;
  const t = term.toLowerCase();
  const hit = (val) => String(val || '').toLowerCase().includes(t);

  if (hit(row.title) || hit(row.description) || hit(row.page_id)) return true;
  if (!missingFilters.composer && hit(row.composer)) return true;
  if (!missingFilters.tune_type && hit(row.tune_type)) return true;
  if (!missingFilters.key_signature && hit(row.key_signature)) return true;
  if (!missingFilters.region && hit(row.region)) return true;
  if (!missingFilters.time_signature && hit(row.time_signature)) return true;
  if (!missingFilters.custom && customMissingField && hit(row[customMissingField])) return true;

  return false;
}

function anyMissingFieldFilterActive(missingFilters, filters) {
  return Boolean(filters.missingOnly) || Object.values(missingFilters).some(Boolean);
}

/** Whether a row qualifies for the missing-field filter at snapshot time. */
function rowQualifiesForMissingFilters(row, missingFilters, customMissingField, filters) {
  if (filters.missingOnly) {
    const hasCoreMissing =
      isMissing(row.composer) || isMissing(row.tune_type) || isMissing(row.key_signature);
    if (!hasCoreMissing) return false;
  }
  if (missingFilters.composer && !isMissing(row.composer)) return false;
  if (missingFilters.tune_type && !isMissing(row.tune_type)) return false;
  if (missingFilters.key_signature && !isMissing(row.key_signature)) return false;
  if (missingFilters.region && !isMissing(row.region)) return false;
  if (missingFilters.time_signature && !isMissing(row.time_signature)) return false;
  if (missingFilters.custom && customMissingField && !isMissing(row[customMissingField])) return false;
  return true;
}

function computeMissingFilterRowIds(rowList, missingFilters, customMissingField, filters) {
  const ids = new Set();
  for (const row of rowList) {
    if (rowQualifiesForMissingFilters(row, missingFilters, customMissingField, filters)) {
      ids.add(row._id);
    }
  }
  return ids;
}

const COL_WIDTHS = {
  page_id: 130, title: 220, description: 260, also_knownas: 170,
  composer: 160, region: 120, tune_type: 110, key_signature: 110,
  time_signature: 90, artist: 150, recording_album: 170, collection_book: 170,
  TrackNo: 70, PageNo: 70, year: 70, product_names: 200,
  notation_asset_ids: 95, notation_asset_filenames: 160,
  audio_asset_ids: 95, audio_asset_filenames: 160,
  source_url: 210, tune_page_url: 210,
  tags: 300,
  cbfiddle_search_url: 180, thesession_search_url: 180,
  abcnotation_search_url: 180, google_cse_search_url: 180,
  updated_at: 140,
};

// Format a DB timestamp for display
function fmtDate(v) {
  if (!v) return '';
  try {
    const d = new Date(v.includes('T') ? v : v.replace(' ', 'T') + 'Z');
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch (_) { return v; }
}
const colW = (h) => COL_WIDTHS[h] || 140;

const REVIEW_FIELDS = new Set(['composer', 'tune_type', 'key_signature', 'time_signature', 'region']);
const URL_COLS = new Set([
  'tune_page_url', 'source_url',
  'cbfiddle_search_url', 'thesession_search_url',
  'abcnotation_search_url', 'google_cse_search_url'
]);

/** DB/API column key → table header text (staging column stays `product_names`). */
const COLUMN_DISPLAY_LABELS = {
  product_names: 'Product IDs',
};

function splitHeaderLabel(header) {
  const display = COLUMN_DISPLAY_LABELS[header] != null ? COLUMN_DISPLAY_LABELS[header] : header;
  const normalized = String(display || '')
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/\s+/g, '_');
  const parts = normalized.split('_').filter(Boolean);
  if (parts.length <= 2) return parts;
  return [parts.slice(0, 2).join(' '), parts.slice(2).join(' ')];
}

/** Append numeric id to a comma-separated list (notation/audio asset IDs or product IDs); null if duplicate / invalid */
function mergeAssetIdIntoCsv(csv, rawId) {
  const aid = Number(rawId);
  if (Number.isNaN(aid) || aid <= 0) return null;
  const cur = String(csv || '').trim();
  const ids = cur
    ? cur.split(/[,;\s]+/).map((s) => parseInt(s, 10)).filter((n) => !Number.isNaN(n) && n > 0)
    : [];
  if (ids.includes(aid)) return null;
  return [...ids, aid].join(', ');
}

/** Parse comma/semicolon/whitespace-separated numeric IDs (product_names, asset lists). */
function parseProductIdsFromCsv(csv) {
  const cur = String(csv || '').trim();
  if (!cur) return [];
  return cur.split(/[,;\s]+/).map((s) => parseInt(s.trim(), 10)).filter((n) => !Number.isNaN(n) && n > 0);
}

/** page_id values for Dangerous zone “load from tune pages” filter (max 2000, unique). */
function parsePullPageIdsInput(raw) {
  if (raw == null || !String(raw).trim()) return [];
  return [...new Set(String(raw).split(/[\s,;|]+/).map((s) => s.trim()).filter(Boolean))].slice(0, 2000);
}

const thumbWrap = {
  display: 'inline-block',
  lineHeight: 0,
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  background: 'var(--color-surface)',
  overflow: 'hidden',
};

function TuneMetaNotationThumbLink({ id }) {
  const [phase, setPhase] = useState(0);
  const src =
    phase === 0 ? `/api/assets/${id}/thumbnail` : phase === 1 ? `/api/assets/${id}/download` : null;
  return (
    <Link
      to={`/admin/assets?assetId=${id}`}
      title={`Notation asset #${id} — open in Assets admin`}
      style={thumbWrap}
    >
      {src ? (
        <img
          src={src}
          alt=""
          style={{
            maxHeight: 64,
            maxWidth: 140,
            objectFit: 'contain',
            display: 'block',
          }}
          onError={() => setPhase((p) => Math.min(p + 1, 2))}
        />
      ) : (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 48,
            minHeight: 48,
            fontSize: 11,
            padding: 4,
          }}
        >
          #{id}
        </span>
      )}
    </Link>
  );
}

/** Notation assets → admin asset editor */
function TuneNotationThumbnails({ idsCsv }) {
  const ids = useMemo(() => parseProductIdsFromCsv(idsCsv), [idsCsv]);
  if (ids.length === 0) return null;
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        marginTop: 8,
        alignItems: 'center',
      }}
    >
      {ids.map((id) => (
        <TuneMetaNotationThumbLink key={id} id={id} />
      ))}
    </div>
  );
}

function TuneMetaAudioThumbLink({ id }) {
  const [iconOnly, setIconOnly] = useState(false);
  return (
    <Link
      to={`/admin/assets?assetId=${id}`}
      title={`Audio asset #${id} — open in Assets admin`}
      style={{
        ...thumbWrap,
        minWidth: 48,
        minHeight: 48,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {iconOnly ? (
        <span style={{ fontSize: 22 }} aria-hidden>🎵</span>
      ) : (
        <img
          src={`/api/assets/${id}/thumbnail`}
          alt=""
          style={{ maxHeight: 56, maxWidth: 120, objectFit: 'contain', display: 'block' }}
          onError={() => setIconOnly(true)}
        />
      )}
    </Link>
  );
}

/** Audio assets → admin asset editor (thumbnail or generic tile) */
function TuneAudioThumbnails({ idsCsv }) {
  const ids = useMemo(() => parseProductIdsFromCsv(idsCsv), [idsCsv]);
  if (ids.length === 0) return null;
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        marginTop: 8,
        alignItems: 'center',
      }}
    >
      {ids.map((id) => (
        <TuneMetaAudioThumbLink key={id} id={id} />
      ))}
    </div>
  );
}

/** Product covers → public product page */
function TuneProductThumbnails({ idsCsv, catalog }) {
  const ids = useMemo(() => parseProductIdsFromCsv(idsCsv), [idsCsv]);
  if (ids.length === 0) return null;
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 10,
        marginTop: 8,
        alignItems: 'flex-start',
      }}
    >
      {ids.map((id) => {
        const entry = catalog?.get(String(id));
        const productStub = {
          id,
          name: entry?.name || `Product #${id}`,
          image: entry?.image || '',
        };
        const src = getProductImageUrlSync(productStub);
        return (
          <Link
            key={id}
            to={`/product/${id}`}
            title={entry?.name ? `${entry.name} (#${id})` : `Product #${id}`}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 4,
              maxWidth: 88,
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            <img
              src={src}
              alt=""
              style={{
                width: 56,
                height: 56,
                objectFit: 'cover',
                borderRadius: 8,
                border: '1px solid var(--color-border)',
                background: 'var(--color-surface)',
              }}
              onError={(e) => {
                e.currentTarget.src = '/images/placeholder.jpg';
              }}
            />
            <span
              style={{
                fontSize: 10,
                lineHeight: 1.2,
                textAlign: 'center',
                color: 'var(--color-text-secondary)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                width: '100%',
              }}
            >
              {entry?.name || `#${id}`}
            </span>
          </Link>
        );
      })}
    </div>
  );
}

export default function AdminTuneMetadata() {
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [filters, setFilters] = useState({
    region: 'All', tuneType: 'All', key: 'All', missingOnly: false
  });
  const [missingFilters, setMissingFilters] = useState({
    composer: false, tune_type: false, key_signature: false,
    region: false, time_signature: false, custom: false,
  });
  const [customMissingField, setCustomMissingField] = useState('also_knownas');
  /** Frozen row _id set while missing-field filters are active — stays fixed until filters change. */
  const [frozenMissingRowIds, setFrozenMissingRowIds] = useState(null);
  const missingSnapshotRef = useRef({ key: '', ids: null, rowsLength: 0 });
  const [sortConfig, setSortConfig] = useState({ key: 'title', direction: 'asc' });
  const TABLE_PAGE_SIZES = [50, 100, 200];
  const [tablePageSize, setTablePageSize] = useState(50);
  const [tablePage, setTablePage] = useState(1);
  /** Product / notation / audio preview tiles in the grid (toolbar toggle). */
  const [showThumbnails, setShowThumbnails] = useState(true);
  const [fieldOptionPresets, setFieldOptionPresets] = useState(DEFAULT_TUNE_METADATA_FIELD_OPTIONS);
  /** Main UI: table + filters vs. import / automation / dangerous actions. */
  const [reviewerTab, setReviewerTab] = useState('review');

  /** `{ pageId, rowId, field, fileType }` when picking an asset for notation/audio IDs */
  const [assetSearchModal, setAssetSearchModal] = useState(null);
  /** `{ pageId, rowId }` when picking a related product for product_names */
  const [productSearchModal, setProductSearchModal] = useState(null);
  /** `null` = loading; `Map` id string → `{ name, image }` for labels, auto-tag, thumbnails */
  const [productCatalog, setProductCatalog] = useState(null);

  // API state
  const [loading, setLoading] = useState(true);
  const [rowsLoadError, setRowsLoadError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null); // last upload summary
  const [saveStatus, setSaveStatus] = useState(''); // '', 'saving', 'saved', 'error'
  const saveTimerRef = useRef(null);

  // Upload mode picker state
  const [showUploadPicker, setShowUploadPicker] = useState(false);
  const [uploadMode, setUploadMode] = useState('replace');
  const fileInputRef = useRef(null);

  // Sync staging ↔ live content_pages (tune-notation / abc-notation)
  const [showSyncPullPicker, setShowSyncPullPicker] = useState(false);
  const [showSyncPushPicker, setShowSyncPushPicker] = useState(false);
  const [syncPullMode, setSyncPullMode] = useState('upsert');
  const [syncPushMode, setSyncPushMode] = useState('overwrite');
  const [syncWorking, setSyncWorking] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [showDangerousZone, setShowDangerousZone] = useState(false);
  const [applyPushPreview, setApplyPushPreview] = useState(null);
  const [applyPushPreviewLoading, setApplyPushPreviewLoading] = useState(false);
  const [dangerousPullPageIdsText, setDangerousPullPageIdsText] = useState('');
  const [dangerousPullTitleContains, setDangerousPullTitleContains] = useState('');

  // Research state
  const [repairing, setRepairing] = useState(false);
  const [repairStatus, setRepairStatus] = useState('');
  const [batchSize, setBatchSize] = useState(5);
  const [pendingResearch, setPendingResearch] = useState({});

  // Add-column state
  const [addingCol, setAddingCol] = useState(false);
  const [newColName, setNewColName] = useState('');

  // ── API helpers ───────────────────────────────────────────────────────
  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const showSaved = useCallback(() => {
    setSaveStatus('saved');
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => setSaveStatus(''), 2000);
  }, []);

  /** Reset search + table filters so newly loaded rows are visible (same idea as after CSV upload). */
  const resetReviewerViewFilters = useCallback(() => {
    setSearch('');
    setFilters({ region: 'All', tuneType: 'All', key: 'All', missingOnly: false });
    setMissingFilters({
      composer: false,
      tune_type: false,
      key_signature: false,
      region: false,
      time_signature: false,
      custom: false,
    });
    missingSnapshotRef.current = { key: '', ids: null, rowsLength: 0 };
    setFrozenMissingRowIds(null);
    setTablePage(1);
  }, []);

  // ── Load rows from DB on mount ────────────────────────────────────────
  const loadFromApi = useCallback(async () => {
    try {
      setLoading(true);
      setRowsLoadError('');
      const resp = await fetch(tuneMetaApiUrl('/api/tune-metadata/rows'), {
        headers: authHeader(),
        cache: 'no-store',
      });
      const body = await tuneMetaParseApiJson(resp);
      if (!resp.ok) throw new Error(body.error || body.hint || `HTTP ${resp.status}`);
      const { rows: data, columns } = body;
      if (!Array.isArray(data)) throw new Error('Invalid API response: rows is not an array');
      const rowsWithId = data.map(r => ({ ...r, _id: r.page_id || r.id }));
      setHeaders(columns || []);
      setRows(rowsWithId);
      if (columns?.length) {
        setCustomMissingField(prev => columns.includes(prev) ? prev : (columns[0] || ''));
      }
    } catch (err) {
      console.error('[AdminTuneMetadata] load failed', err);
      setRowsLoadError(err.message || 'Could not load staging rows from the server.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadFromApi(); }, [loadFromApi]);

  useEffect(() => {
    fetchTuneSiteOptions().then((opts) => {
      if (opts?.tuneMetadataFieldOptions) {
        setFieldOptionPresets(opts.tuneMetadataFieldOptions);
      }
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        let resp = await fetch(tuneMetaApiUrl('/api/admin/products'), {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (resp.status === 401 || resp.status === 403) {
          resp = await fetch(tuneMetaApiUrl('/api/products'));
        }
        if (!resp.ok) {
          if (!cancelled) setProductCatalog(new Map());
          return;
        }
        const data = await resp.json();
        if (cancelled) return;
        const m = new Map();
        (Array.isArray(data) ? data : []).forEach((p) => {
          if (p != null && p.id != null) {
            const nm = p.name != null ? String(p.name).trim() : '';
            const im = p.image != null ? String(p.image).trim() : '';
            m.set(String(p.id), { name: nm, image: im });
          }
        });
        setProductCatalog(m);
      } catch (e) {
        console.warn('[AdminTuneMetadata] product catalog load failed', e);
        if (!cancelled) setProductCatalog(new Map());
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Dry-run preview when Apply panel is open (Dangerous zone)
  useEffect(() => {
    if (!showDangerousZone || !showSyncPushPicker || syncWorking || rows.length === 0) return undefined;
    let cancelled = false;
    (async () => {
      setApplyPushPreviewLoading(true);
      setApplyPushPreview(null);
      try {
        const resp = await fetch(tuneMetaApiUrl('/api/tune-metadata/sync/to-content-pages'), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ mode: syncPushMode, dryRun: true }),
        });
        const json = await tuneMetaParseApiJson(resp);
        if (cancelled) return;
        if (!resp.ok) {
          setApplyPushPreview({ error: json.error || json.hint || `HTTP ${resp.status}` });
          return;
        }
        setApplyPushPreview(json);
      } catch (e) {
        if (!cancelled) setApplyPushPreview({ error: e.message });
      } finally {
        if (!cancelled) setApplyPushPreviewLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showDangerousZone, showSyncPushPicker, syncPushMode, rows.length, syncWorking]);

  // ── Upload CSV → server → reload ─────────────────────────────────────
  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // allow re-selection of same file
    setShowUploadPicker(false);
    setUploading(true);
    setUploadResult(null);
    try {
      const form = new FormData();
      form.append('csv', file);
      const resp = await fetch(tuneMetaApiUrl(`/api/tune-metadata/upload?mode=${uploadMode}`), {
        method: 'POST',
        headers: authHeader(),
        body: form,
      });
      const json = await tuneMetaParseApiJson(resp);
      if (!resp.ok) throw new Error(json.error || `HTTP ${resp.status}`);
      setUploadResult({ mode: uploadMode, ...json });
      await loadFromApi();
      resetReviewerViewFilters();
      setPendingResearch({});
      setExpandedId(null);
    } catch (err) {
      alert(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  // ── Save a single cell on blur → PUT to API ───────────────────────────
  const saveRow = useCallback(async (pageId, field, value) => {
    setSaveStatus('saving');
    try {
      const resp = await fetch(tuneMetaApiUrl(`/api/tune-metadata/row/${encodeURIComponent(pageId)}`), {
        method: 'PUT',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      });
      const json = await tuneMetaParseApiJson(resp);
      if (!resp.ok) throw new Error(json.error || `HTTP ${resp.status}`);
      // Update the updated_at field in local state so the date column refreshes
      if (json.updated_at) {
        setRows(prev => prev.map(r =>
          r._id === pageId ? { ...r, updated_at: json.updated_at } : r
        ));
      }
      showSaved();
    } catch (err) {
      setSaveStatus('error');
      console.error('[AdminTuneMetadata] save failed', err);
    }
  }, [showSaved]);

  const updateCell = (id, field, value) =>
    setRows(prev => prev.map(r => r._id === id ? { ...r, [field]: value } : r));

  const onCellBlur = useCallback((pageId, field, value) => {
    if (pageId) saveRow(pageId, field, value);
  }, [saveRow]);

  // ── Delete a single row ───────────────────────────────────────────────
  const deleteRow = async (id) => {
    setRows(prev => prev.filter(r => r._id !== id));
    setExpandedId(prev => (prev === id ? null : prev));
    try {
      const resp = await fetch(tuneMetaApiUrl(`/api/tune-metadata/row/${encodeURIComponent(id)}`), {
        method: 'DELETE', headers: authHeader(),
      });
      const body = await tuneMetaParseApiJson(resp).catch(() => ({}));
      if (!resp.ok) {
        await loadFromApi();
        alert(body.error || `Could not delete row (HTTP ${resp.status})`);
        return;
      }
      if (body.changes === 0) {
        await loadFromApi();
        alert('No matching row was deleted (refresh synced from server).');
      }
    } catch (err) {
      console.error('[AdminTuneMetadata] delete row failed', err);
      await loadFromApi();
      alert(err.message || 'Delete failed');
    }
  };

  // ── Clear entire table ────────────────────────────────────────────────
  const clearTable = async () => {
    if (!window.confirm(`Clear all ${rows.length} rows from the database? This cannot be undone.`)) return;
    try {
      const resp = await fetch(tuneMetaApiUrl('/api/tune-metadata/rows'), { method: 'DELETE', headers: authHeader() });
      if (!resp.ok) {
        const j = await tuneMetaParseApiJson(resp);
        throw new Error(j.error || `HTTP ${resp.status}`);
      }
      setRows([]);
      setHeaders([]);
      setExpandedId(null);
      setPendingResearch({});
    } catch (err) {
      alert(`Clear failed: ${err.message}`);
    }
  };

  const deleteColumn = (col) => {
    setHeaders(prev => prev.filter(h => h !== col));
    if (customMissingField === col) {
      setCustomMissingField(prev => {
        const remaining = headers.filter(h => h !== col);
        return remaining.length ? remaining[0] : '';
      });
    }
    if (sortConfig.key === col) setSortConfig({ key: null, direction: 'asc' });
  };

  const addColumn = () => {
    const name = newColName.trim().replace(/\s+/g, '_');
    if (!name || headers.includes(name)) return;
    setHeaders(prev => [...prev, name]);
    setRows(prev => prev.map(r => ({ ...r, [name]: '' })));
    setNewColName('');
    setAddingCol(false);
  };

  // ── Pending research helpers ─────────────────────────────────────────
  const updatePending = (id, field, value) =>
    setPendingResearch(prev => ({
      ...prev,
      [id]: { ...prev[id], proposed: { ...prev[id].proposed, [field]: value } }
    }));

  const applyPending = () => {
    setRows(prev => prev.map(r => {
      const p = pendingResearch[r._id];
      if (!p) return r;
      return { ...r, ...p.proposed };
    }));
    setPendingResearch({});
  };

  const discardPending = () => setPendingResearch({});

  const requestSort = (key) =>
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      // Date column defaults to newest-first (desc)
      return { key, direction: key === 'updated_at' ? 'desc' : 'asc' };
    });

  const pullFromContentPages = async () => {
    setSyncWorking(true);
    setSyncResult(null);
    try {
      const page_ids = parsePullPageIdsInput(dangerousPullPageIdsText);
      const title_contains = dangerousPullTitleContains.trim().slice(0, 240);
      const resp = await fetch(
        tuneMetaApiUrl(`/api/tune-metadata/sync/from-content-pages?mode=${encodeURIComponent(syncPullMode)}`),
        {
          method: 'POST',
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ page_ids, title_contains }),
        }
      );
      const json = await tuneMetaParseApiJson(resp);
      if (!resp.ok) throw new Error(json.error || json.hint || `HTTP ${resp.status}`);
      setSyncResult(json);
      setShowSyncPullPicker(false);
      await loadFromApi();
      resetReviewerViewFilters();
      setPendingResearch({});
      setExpandedId(null);
      setShowDangerousZone(false);
    } catch (err) {
      alert(`Load from tune pages failed: ${err.message}`);
    } finally {
      setSyncWorking(false);
    }
  };

  const pushToContentPages = async () => {
    const msg =
      syncPushMode === 'overwrite'
        ? 'Overwrite live tune pages from this table? Rows whose page_id does not exist yet will get a new tune page. On updates, empty cells can clear optional fields. related_content is preserved unless stored in extra columns.'
        : syncPushMode === 'create_missing'
          ? 'Create new live tune pages only for staging rows whose page_id does not exist yet? Existing pages will not be modified. This writes new rows to content_pages.'
          : 'Fill only empty fields on live tune pages from this table? Existing title, description, tags, and metadata are kept when already set.';
    if (!window.confirm(msg)) return;
    setSyncWorking(true);
    setSyncResult(null);
    try {
      const resp = await fetch(tuneMetaApiUrl('/api/tune-metadata/sync/to-content-pages'), {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode: syncPushMode }),
      });
      const json = await tuneMetaParseApiJson(resp);
      if (!resp.ok) throw new Error(json.error || json.hint || `HTTP ${resp.status}`);
      setSyncResult(json);
      setShowSyncPushPicker(false);
    } catch (err) {
      alert(`Apply to tune pages failed: ${err.message}`);
    } finally {
      setSyncWorking(false);
    }
  };

  const openAssetSearchForField = (r, field) => {
    if (field !== 'notation_asset_ids' && field !== 'audio_asset_ids') return;
    const fileType = field === 'notation_asset_ids' ? 'image' : 'music';
    setAssetSearchModal({
      pageId: r.page_id,
      rowId: r._id,
      field,
      fileType,
    });
  };

  const handleAssetSearchSelect = (asset) => {
    if (!assetSearchModal || asset == null || asset.id == null) return;
    const { pageId, rowId, field } = assetSearchModal;
    const row = rows.find((x) => x._id === rowId);
    const newVal = row ? mergeAssetIdIntoCsv(row[field], asset.id) : null;
    setAssetSearchModal(null);
    if (newVal == null) return;
    updateCell(rowId, field, newVal);
    saveRow(pageId, field, newVal);
  };

  const openProductSearchForRow = (r) => {
    setProductSearchModal({ pageId: r.page_id, rowId: r._id });
  };

  const handleProductSearchSelect = (product) => {
    if (!productSearchModal || product == null || product.id == null) return;
    const { pageId, rowId } = productSearchModal;
    const row = rows.find((x) => x._id === rowId);
    const field = 'product_names'; // DB column: comma-separated numeric product IDs
    const newVal = row ? mergeAssetIdIntoCsv(row[field], product.id) : null;
    setProductSearchModal(null);
    if (newVal == null) return;
    updateCell(rowId, field, newVal);
    saveRow(pageId, field, newVal);
  };

  // ── Web Research (Batch) ─────────────────────────────────────────────
  // Queries TheSession + CBFiddle via backend proxy, then stages results for review.
  const RESEARCH_FIELDS = ['tune_type', 'key_signature', 'composer', 'also_knownas'];
  const LINK_MAP = {
    cbfiddle_search:    '_cbfiddle_search_url',
    thesession_search:  '_thesession_search_url',
    abcnotation_search: '_abcnotation_search_url',
    google_cse_search:  '_google_cse_search_url',
    cbfiddle_match:     '_cbfiddle_match_url',
    thesession_match:   '_thesession_match_url',
  };

  const researchBatch = async () => {
    const tunesToRepair = filtered
      .filter(r => isMissing(r.composer) || isMissing(r.tune_type) || isMissing(r.key_signature))
      .slice(0, batchSize);

    if (tunesToRepair.length === 0) {
      setRepairStatus('All visible tunes already have complete core metadata!');
      setTimeout(() => setRepairStatus(''), 3000);
      return;
    }

    setRepairing(true);
    setPendingResearch({});
    const collected = {};

    for (let i = 0; i < tunesToRepair.length; i++) {
      const tune = tunesToRepair[i];
      setRepairStatus(`Researching "${tune.title}" (${i + 1}/${tunesToRepair.length})…`);

      try {
        const params = new URLSearchParams({ title: tune.title });
        if (tune.region) params.set('region', tune.region);
        if (tune.description) params.set('description', tune.description.slice(0, 200));

        const token = localStorage.getItem('token');
        const resp = await fetch(`/api/content/research-tune?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const result = await resp.json();

        // Build proposed: only fill empty fields
        const proposed = {};
        if (isMissing(tune.tune_type) && !isMissing(result.tune_type))       proposed.tune_type = result.tune_type;
        if (isMissing(tune.key_signature) && !isMissing(result.key_signature)) proposed.key_signature = result.key_signature;
        if (isMissing(tune.composer) && !isMissing(result.composer))           proposed.composer = result.composer;
        if (isMissing(tune.also_knownas) && result.also_knownas)               proposed.also_knownas = result.also_knownas;

        // Store links (always, even if no field proposals)
        Object.entries(LINK_MAP).forEach(([k, storeKey]) => {
          if (result.links?.[k]) proposed[storeKey] = result.links[k];
        });

        collected[tune._id] = {
          title: tune.title,
          original: { tune_type: tune.tune_type, key_signature: tune.key_signature, composer: tune.composer, also_knownas: tune.also_knownas },
          proposed,
          sources: result.sources || [],
        };
      } catch (err) {
        console.error(`Research failed for: ${tune.title}`, err.message);
        collected[tune._id] = { title: tune.title, original: {}, proposed: {}, sources: [], error: err.message };
      }

      if (i < tunesToRepair.length - 1) {
        await new Promise(res => setTimeout(res, 600));
      }
    }

    setPendingResearch(collected);
    setRepairing(false);
    setRepairStatus('');
  };

  // ── Auto-populate tags ───────────────────────────────────────────────
  const AUTO_TAG_SOURCES = [
    { key: 'base',            label: 'Base tags',       example: '→ tune, notation, sheet-music',       always: true },
    { key: 'tune_type',       label: 'Tune type',       example: '→ reel / jig / strathspey…' },
    { key: 'key_signature',   label: 'Key signature',   example: '→ a-major / d-major…' },
    { key: 'region',          label: 'Region',          example: '→ cape-breton / scottish…' },
    { key: 'time_signature',  label: 'Time signature',  example: '→ time-6-8…' },
    { key: 'composer',        label: 'Composer',        example: '→ jerry-holland…' },
    { key: 'collection_book', label: 'Collection book', example: '→ (slugified title)' },
    { key: 'product_names', label: 'Product IDs', example: '→ slug from resolved product titles' },
  ];

  const [showAutoTagPanel, setShowAutoTagPanel] = useState(false);
  const [autoTagSources, setAutoTagSources]     = useState(new Set(['base', 'tune_type', 'key_signature', 'region']));
  const [autoTagMode, setAutoTagMode]           = useState('filtered'); // 'filtered' | 'all' | 'empty_only'
  const [autoTagMerge, setAutoTagMerge]         = useState(true);       // true = merge, false = replace
  const [autoTagProgress, setAutoTagProgress]   = useState('');         // '' | 'processing...'
  const [autoTagResult, setAutoTagResult]       = useState(null);       // { updated, skipped }

  function toTagSlug(v) {
    return String(v || '').trim().toLowerCase()
      .replace(/[/\\]/g, '-')
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  function buildAutoTags(row, sources) {
    const derived = new Set();
    if (sources.has('base')) {
      ['tune', 'notation', 'sheet-music'].forEach(t => derived.add(t));
    }
    if (sources.has('tune_type') && !isMissing(row.tune_type)) {
      const s = toTagSlug(row.tune_type);
      if (s) derived.add(s);
    }
    if (sources.has('key_signature') && !isMissing(row.key_signature)) {
      const s = toTagSlug(row.key_signature);
      if (s) derived.add(s);
    }
    if (sources.has('region') && !isMissing(row.region)) {
      const s = toTagSlug(row.region);
      if (s) derived.add(s);
    }
    if (sources.has('time_signature') && !isMissing(row.time_signature)) {
      const raw = String(row.time_signature).trim();
      const s = 'time-' + raw.replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      if (s !== 'time-') derived.add(s);
    }
    if (sources.has('composer') && !isMissing(row.composer)) {
      const s = toTagSlug(row.composer);
      if (s) derived.add(s);
    }
    if (sources.has('collection_book') && !isMissing(row.collection_book)) {
      const s = toTagSlug(row.collection_book);
      // Skip if slug is too long (> 60 chars) — likely a sentence, not a useful tag
      if (s && s.length <= 60) derived.add(s);
    }
    if (sources.has('product_names') && productCatalog && productCatalog.size > 0) {
      for (const id of parseProductIdsFromCsv(row.product_names)) {
        const name = productCatalog.get(String(id))?.name;
        if (!name) continue;
        const s = toTagSlug(name);
        if (s && s.length <= 60) derived.add(s);
      }
    }
    return [...derived];
  }

  // ── Derived state ────────────────────────────────────────────────────
  const uniqueValues = useMemo(() => {
    const regions = new Set(), types = new Set(), keys = new Set(), times = new Set();
    rows.forEach(r => {
      if (!isMissing(r.region))         regions.add(r.region);
      if (!isMissing(r.tune_type))      types.add(r.tune_type);
      if (!isMissing(r.key_signature))  keys.add(r.key_signature);
      if (!isMissing(r.time_signature)) times.add(r.time_signature);
    });
    return {
      regions: [...regions].sort(), types: [...types].sort(),
      keys: [...keys].sort(),       times: [...times].sort(),
    };
  }, [rows]);

  // Presets + all data values merged — used by SelectWithAdd cells
  const fieldAllOptions = useMemo(() => {
    const merge = (preset, dataVals) => {
      const s = new Set([...preset, ...dataVals]);
      return [...s].sort((a, b) => a.localeCompare(b));
    };
    return {
      region:         merge(fieldOptionPresets.region,         uniqueValues.regions),
      tune_type:      merge(fieldOptionPresets.tune_type,       uniqueValues.types),
      key_signature:  merge(fieldOptionPresets.key_signature,   uniqueValues.keys),
      time_signature: [...fieldOptionPresets.time_signature],
    };
  }, [uniqueValues, fieldOptionPresets]);

  const searchPlaceholder = useMemo(() => {
    const skipped = [];
    if (missingFilters.composer) skipped.push('composer');
    if (missingFilters.tune_type) skipped.push('type');
    if (missingFilters.key_signature) skipped.push('key');
    if (missingFilters.region) skipped.push('region');
    if (missingFilters.time_signature) skipped.push('time');
    if (missingFilters.custom && customMissingField) skipped.push(customMissingField.replace(/_/g, ' '));
    if (skipped.length === 0) return 'Search titles, composers, page IDs…';
    return `Search titles, page IDs… (${skipped.join(', ')} excluded — missing-field filter)`;
  }, [missingFilters, customMissingField]);

  const missingFilterKey = useMemo(
    () => JSON.stringify({ mf: missingFilters, custom: customMissingField, core: filters.missingOnly }),
    [missingFilters, customMissingField, filters.missingOnly]
  );

  // Lock the working set when missing-field filters are enabled (edits must not drop rows).
  useEffect(() => {
    if (!anyMissingFieldFilterActive(missingFilters, filters)) {
      missingSnapshotRef.current = { key: '', ids: null, rowsLength: 0 };
      setFrozenMissingRowIds(null);
      return;
    }

    const snap = missingSnapshotRef.current;
    const keyChanged = snap.key !== missingFilterKey;
    const needsInitialFill =
      snap.key === missingFilterKey &&
      snap.ids !== null &&
      snap.ids.size === 0 &&
      rows.length > snap.rowsLength;

    if (!keyChanged && !needsInitialFill && snap.ids !== null) {
      return;
    }

    const ids = computeMissingFilterRowIds(rows, missingFilters, customMissingField, filters);
    missingSnapshotRef.current = { key: missingFilterKey, ids, rowsLength: rows.length };
    setFrozenMissingRowIds(ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- snapshot only on filter change or row count growth, not on cell edits
  }, [missingFilterKey, rows.length, missingFilters, customMissingField, filters]);

  const stats = useMemo(() => {
    if (!rows.length) return null;
    return {
      total: rows.length,
      missingComposer: rows.filter(r => isMissing(r.composer)).length,
      missingType: rows.filter(r => isMissing(r.tune_type)).length,
      missingKey: rows.filter(r => isMissing(r.key_signature)).length,
      complete: rows.filter(r =>
        !isMissing(r.composer) && !isMissing(r.tune_type) && !isMissing(r.key_signature)
      ).length
    };
  }, [rows]);

  const filtered = useMemo(() => rows.filter(r => {
    const term = search.trim();
    const matchSearch = rowMatchesSearchTerm(r, term, missingFilters, customMissingField);
    const matchRegion = filters.region === 'All' || r.region === filters.region;
    const matchType = filters.tuneType === 'All' || r.tune_type === filters.tuneType;
    const matchKey = filters.key === 'All' || r.key_signature === filters.key;
    const matchMissingSnapshot =
      frozenMissingRowIds === null || frozenMissingRowIds.has(r._id);
    return matchSearch && matchRegion && matchType && matchKey && matchMissingSnapshot;
  }), [rows, search, filters, missingFilters, customMissingField, frozenMissingRowIds]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    const { key, direction } = sortConfig;
    copy.sort((a, b) => {
      const va = a[key] || '';
      const vb = b[key] || '';
      // Date comparison for updated_at
      if (key === 'updated_at') {
        const da = va ? new Date(va).getTime() : 0;
        const db_ = vb ? new Date(vb).getTime() : 0;
        return direction === 'asc' ? da - db_ : db_ - da;
      }
      const sa = String(va).toLowerCase();
      const sb = String(vb).toLowerCase();
      return direction === 'asc' ? sa.localeCompare(sb) : sb.localeCompare(sa);
    });
    return copy;
  }, [filtered, sortConfig]);

  const tableTotalPages = Math.max(1, Math.ceil(sorted.length / tablePageSize) || 1);

  useEffect(() => {
    setTablePage((p) => {
      const tp = Math.max(1, Math.ceil(sorted.length / tablePageSize) || 1);
      return Math.min(Math.max(1, p), tp);
    });
  }, [sorted.length, tablePageSize]);

  const tablePageOffset = (tablePage - 1) * tablePageSize;
  const paginatedSorted = useMemo(
    () => sorted.slice(tablePageOffset, tablePageOffset + tablePageSize),
    [sorted, tablePageOffset, tablePageSize]
  );

  const exportCSV = useCallback(() => {
    if (!sorted.length) return;
    try {
      const csv = toCSV(headers, sorted);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'tune_metadata_export.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(`Export failed: ${err.message}`);
    }
  }, [headers, sorted]);

  // ── Run auto-tagging (needs filtered, defined above) ──────────────────
  const runAutoTagging = useCallback(async () => {
    const targetRows =
      autoTagMode === 'all'        ? rows :
      autoTagMode === 'empty_only' ? rows.filter(r => !r.tags || !r.tags.trim()) :
                                     filtered;

    if (targetRows.length === 0) return;

    setAutoTagProgress(`Processing 0 / ${targetRows.length}…`);
    let updated = 0, skipped = 0;

    for (let i = 0; i < targetRows.length; i++) {
      const row = targetRows[i];
      const derived = buildAutoTags(row, autoTagSources);
      if (derived.length === 0) { skipped++; continue; }

      let newTags;
      if (autoTagMerge) {
        const existing = parseTags(row.tags);
        const merged   = [...new Set([...existing, ...derived])];
        if (merged.length === existing.length && merged.every(t => existing.includes(t))) {
          skipped++;
          continue;
        }
        newTags = merged.join(',');
      } else {
        newTags = derived.join(',');
      }

      setRows(prev => prev.map(r => r._id === row._id ? { ...r, tags: newTags } : r));
      try {
        const resp = await fetch(tuneMetaApiUrl(`/api/tune-metadata/row/${encodeURIComponent(row.page_id)}`), {
          method: 'PUT',
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ tags: newTags }),
        });
        if (resp.ok) {
          const json = await tuneMetaParseApiJson(resp);
          if (json.updated_at) {
            setRows(prev => prev.map(r => r._id === row._id ? { ...r, updated_at: json.updated_at } : r));
          }
          updated++;
        } else { skipped++; }
      } catch (_) { skipped++; }

      setAutoTagProgress(`Processing ${i + 1} / ${targetRows.length}…`);
      if ((i + 1) % 10 === 0) await new Promise(r => setTimeout(r, 0));
    }

    setAutoTagProgress('');
    setAutoTagResult({ updated, skipped, total: targetRows.length });
    setShowAutoTagPanel(false);
  }, [autoTagSources, autoTagMode, autoTagMerge, rows, filtered, productCatalog]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Styles ────────────────────────────────────────────────────────────
  const S = {
    page: { padding: '16px 0' },
    toolbar: { display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 14 },
    pageHeader: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: '10px 14px',
      marginBottom: 14,
      paddingBottom: 12,
      borderBottom: '1px solid var(--color-border)',
    },
    tabRow: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 },
    headerActions: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginLeft: 'auto' },
    statInline: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '10px 22px',
      alignItems: 'center',
      padding: '10px 14px',
      marginBottom: 14,
      background: 'var(--color-surface)',
      border: '1px solid var(--color-border)',
      borderRadius: 10,
      fontSize: 13,
    },
    toolsSection: {
      border: '1px solid var(--color-border)',
      borderRadius: 12,
      padding: '16px 18px',
      marginBottom: 14,
      background: 'var(--color-surface)',
    },
    toolsSectionTitle: {
      fontSize: 13,
      fontWeight: 700,
      margin: '0 0 12px',
      color: 'var(--color-text-primary)',
    },
    statGrid: {
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
      gap: 12, marginBottom: 18
    },
    statCard: {
      background: 'var(--color-surface)',
      border: '1px solid var(--color-border)',
      borderRadius: 12, padding: '14px 16px', boxShadow: '0 1px 3px rgba(0,0,0,.08)'
    },
    statLabel: {
      fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em',
      color: 'var(--color-text-secondary)', marginBottom: 4
    },
    statValue: (accent) => ({ fontSize: 26, fontWeight: 800, color: accent }),
    banner: (bg, border, color) => ({
      padding: '12px 16px', borderRadius: 10, marginBottom: 14,
      background: bg, border: `1px solid ${border}`, color,
      display: 'flex', alignItems: 'center', gap: 10, fontSize: 14
    }),
    batchResult: {
      padding: '12px 16px', borderRadius: 10, marginBottom: 14,
      background: 'var(--color-surface)', border: '1px solid color-mix(in srgb, var(--color-success) 40%, var(--color-border))',
      fontSize: 14
    },
    filterBar: {
      background: 'var(--color-surface)',
      border: '1px solid var(--color-border)',
      borderRadius: 12, padding: '14px 18px',
      display: 'flex', flexWrap: 'wrap', gap: 12,
      alignItems: 'flex-end', marginBottom: 18
    },
    filterLabel: {
      fontSize: 12, fontWeight: 600, marginBottom: 4,
      color: 'var(--color-text-secondary)'
    },
    input: {
      padding: '7px 10px', border: '1px solid var(--color-border)',
      borderRadius: 8, background: 'transparent', color: 'inherit', fontSize: 14, outline: 'none'
    },
    select: {
      padding: '7px 10px', border: '1px solid var(--color-border)',
      borderRadius: 8, background: 'var(--color-surface)',
      color: 'inherit', fontSize: 14, outline: 'none'
    },
    tableWrapper: { overflowX: 'auto' },
    table: {
      width: '100%', borderCollapse: 'collapse',
      background: 'var(--color-surface)',
      border: '1px solid var(--color-border)',
      borderRadius: 12, overflow: 'hidden'
    },
    th: (col) => ({
      padding: '10px 8px', textAlign: 'left',
      background: 'var(--color-surface-hover)',
      borderBottom: '2px solid var(--color-border)',
      fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
      letterSpacing: '.03em', color: 'var(--color-text-secondary)',
      cursor: 'pointer', whiteSpace: 'normal', lineHeight: 1.1,
      wordBreak: 'break-word', minWidth: colW(col)
    }),
    td: { padding: '6px 8px', borderBottom: '1px solid var(--color-border)', verticalAlign: 'top' },
    cell: (h, val) => {
      const bad = REVIEW_FIELDS.has(h) && isMissing(val);
      return {
        width: '100%', minWidth: Math.max(colW(h) - 18, 70),
        padding: '5px 8px',
        border: bad ? '1.5px solid var(--color-error)' : '1px solid var(--color-border)',
        borderRadius: 6, background: 'transparent',
        color: bad ? 'var(--color-error)' : 'inherit',
        fontSize: 13, outline: 'none', boxSizing: 'border-box'
      };
    },
    smallLink: { display: 'inline-block', marginTop: 2, fontSize: 11, color: 'var(--color-accent)', textDecoration: 'underline' },
    expandedPanel: {
      background: 'var(--color-surface-hover)',
      borderBottom: '1px solid var(--color-border)', padding: '14px 18px'
    },
    link: { color: 'var(--color-accent)', textDecoration: 'underline', fontSize: 13, wordBreak: 'break-all' },
    deleteBtn: {
      border: '1px solid var(--color-error)', color: 'var(--color-error)', background: 'transparent',
      borderRadius: 6, padding: '4px 8px', fontSize: 11, cursor: 'pointer'
    },
    empty: {
      textAlign: 'center', padding: 60,
      border: '2px dashed var(--color-border)',
      borderRadius: 16, color: 'var(--color-text-secondary)'
    }
  };

  const STAT_ITEMS = stats ? [
    { label: 'Total',            value: stats.total,           accent: 'var(--color-primary)' },
    { label: 'Missing Composer', value: stats.missingComposer, accent: 'var(--color-warning)' },
    { label: 'Missing Type',     value: stats.missingType,     accent: 'var(--color-error)' },
    { label: 'Missing Key',      value: stats.missingKey,      accent: 'var(--color-info)' },
    { label: 'Complete',         value: stats.complete,        accent: 'var(--color-success)' },
  ] : [];

  // Count incomplete tunes in the currently filtered/visible set
  const incompleteInView = filtered.filter(r =>
    isMissing(r.composer) || isMissing(r.tune_type) || isMissing(r.key_signature)
  ).length;
  const batchTarget = Math.min(batchSize, incompleteInView);

  const leaveDangerousZone = () => {
    setShowDangerousZone(false);
    setShowSyncPullPicker(false);
    setShowSyncPushPicker(false);
    setApplyPushPreview(null);
    setApplyPushPreviewLoading(false);
    setDangerousPullPageIdsText('');
    setDangerousPullTitleContains('');
  };

  return (
    <div style={S.page} className="container admin-tune-metadata-page">
      {syncResult && (
        <div style={{
          padding: '14px 18px', borderRadius: 12, marginBottom: 14,
          background: 'rgba(59,130,246,0.08)', border: '1px solid #93c5fd',
          fontSize: 13,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: syncResult.direction === 'to-content-pages' ? 10 : 0 }}>
            <span>
              <strong>✓ Sync</strong>
              {' · '}
              {syncResult.direction === 'from-content-pages' && (
                <>
                  Loaded <strong>{syncResult.rowsWritten}</strong> row(s) from <strong>{syncResult.sourceTunePages}</strong> tune page(s)
                  ({syncResult.mode}) · <strong>{syncResult.totalInTable}</strong> in table
                  {syncResult.pullFilter &&
                    (syncResult.pullFilter.page_ids_count > 0 || syncResult.pullFilter.title_contains) && (
                    <>
                      {' · '}
                      <span style={{ color: 'var(--color-text-secondary)' }}>
                        filter:{' '}
                        {syncResult.pullFilter.page_ids_count > 0 && (
                          <>{syncResult.pullFilter.page_ids_count} id(s)</>
                        )}
                        {syncResult.pullFilter.page_ids_count > 0 && syncResult.pullFilter.title_contains && ', '}
                        {syncResult.pullFilter.title_contains && (
                          <>{`title match: ${syncResult.pullFilter.title_contains}`}</>
                        )}
                      </span>
                    </>
                  )}
                </>
              )}
              {syncResult.direction === 'to-content-pages' && !syncResult.samples && (
                <>
                  Updated <strong>{syncResult.contentPagesUpdated}</strong> content page(s)
                  ({syncResult.mode})
                  {(syncResult.skippedNoContentPage > 0) && (
                    <> · <strong>{syncResult.skippedNoContentPage}</strong> row(s) had no matching tune page</>
                  )}
                </>
              )}
              {syncResult.direction === 'to-content-pages' && syncResult.skippedUnchanged !== undefined && (
                <>
                  {syncResult.mode === 'create_missing' ? (
                    <>
                      Apply finished (<strong>create_missing</strong>): created{' '}
                      <strong>{syncResult.created ?? syncResult.contentPagesCreated ?? 0}</strong>, skipped existing{' '}
                      <strong>{syncResult.skippedAlreadyExists ?? 0}</strong>
                    </>
                  ) : (
                    <>
                      Apply finished (<strong>{syncResult.mode}</strong>): updated{' '}
                      <strong>{syncResult.contentPagesUpdated ?? syncResult.updated ?? 0}</strong>
                      {(syncResult.created ?? syncResult.contentPagesCreated ?? 0) > 0 && (
                        <>, created <strong>{syncResult.created ?? syncResult.contentPagesCreated}</strong> new</>
                      )}
                    </>
                  )}
                </>
              )}
            </span>
            <button type="button" className="admin-tune-metadata-icon-btn" onClick={() => setSyncResult(null)} style={{ fontSize: 16, flexShrink: 0 }}>✕</button>
          </div>
          {syncResult.direction === 'to-content-pages' && syncResult.skippedUnchanged !== undefined && (
            <ToContentPagesApplyBreakdown payload={syncResult} variant="result" />
          )}
        </div>
      )}

      {showDangerousZone ? (
        <div style={{ maxWidth: 960, margin: '0 auto', padding: '12px 0 48px' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={leaveDangerousZone}
            style={{ fontSize: 13, marginBottom: 16 }}
          >
            ← Back to reviewer
          </button>
          <h2 style={{ margin: '0 0 8px', fontSize: 22 }}>Dangerous zone</h2>
          <p style={{ margin: '0 0 20px', fontSize: 14, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
            Tools here read or write <strong>live</strong> rows in <code>content_pages</code> — the same tune pages the public site uses.
            Loading refreshes this reviewer table from the database; applying pushes your staging edits back. Errors here affect the live tune library.
          </p>
          <div style={{
            border: '2px solid rgba(234, 88, 12, 0.45)',
            borderRadius: 12,
            padding: '16px 18px',
            marginBottom: 20,
            background: 'rgba(234, 88, 12, 0.06)',
          }}>
            <div style={{
              fontSize: 12, fontWeight: 700, color: 'var(--color-warning)', marginBottom: 12,
              textTransform: 'uppercase', letterSpacing: '0.04em',
            }}>
              Live site (content_pages)
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 10 }}>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={syncWorking}
                onClick={() => { setShowSyncPullPicker((p) => !p); setShowSyncPushPicker(false); }}
                style={{ fontSize: 13 }}
              >
                {syncWorking ? '⏳…' : '⬇ Load from tune pages'} ▾
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={syncWorking || rows.length === 0}
                onClick={() => { setShowSyncPushPicker((p) => !p); setShowSyncPullPicker(false); }}
                style={{ fontSize: 13 }}
              >
                {syncWorking ? '⏳…' : '⬆ Apply to tune pages'} ▾
              </button>
            </div>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.45 }}>
              Load fills this reviewer table from DB tune pages; after a successful load you return to the main reviewer so you can see the rows.
              Apply writes reviewer rows back to the same pages the app uses (metadata + title/description).
            </p>
          </div>

      {/* ── Sync: load from content_pages ── */}
      {showSyncPullPicker && !syncWorking && (
        <div style={{
          border: '1px solid var(--color-border)',
          borderRadius: 12, padding: '16px 20px', marginBottom: 14,
          background: 'var(--color-surface)',
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>
            Load from live tune pages into this table
          </div>
          <div style={{
            marginBottom: 14,
            padding: '12px 14px',
            borderRadius: 10,
            border: '1px solid var(--color-border)',
            background: 'var(--color-surface-hover)',
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, color: 'var(--color-text-primary)' }}>
              Filter (optional)
            </div>
            <p style={{ margin: '0 0 10px', fontSize: 12, color: 'var(--color-text-secondary)', lineHeight: 1.45 }}>
              Leave both fields empty to load <strong>all</strong> tune-notation and ABC pages. If you set one or both, only rows that match are loaded (both act as <strong>AND</strong> when both are set).
            </p>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
              Page IDs
            </label>
            <textarea
              value={dangerousPullPageIdsText}
              onChange={(e) => setDangerousPullPageIdsText(e.target.value)}
              placeholder={'e.g. my-tune-slug\nanother-tune\nor: a, b, c'}
              rows={4}
              style={{
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                padding: '8px 10px',
                borderRadius: 8,
                border: '1px solid var(--color-border)',
                fontSize: 12,
                fontFamily: 'ui-monospace, monospace',
                background: 'var(--color-surface)',
                color: 'inherit',
                marginBottom: 12,
              }}
            />
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
              Title contains
            </label>
            <input
              type="text"
              value={dangerousPullTitleContains}
              onChange={(e) => setDangerousPullTitleContains(e.target.value)}
              placeholder="Substring match on page title (case-insensitive)"
              style={{
                width: '100%',
                maxWidth: 420,
                boxSizing: 'border-box',
                padding: '8px 10px',
                borderRadius: 8,
                border: '1px solid var(--color-border)',
                fontSize: 13,
                background: 'var(--color-surface)',
                color: 'inherit',
              }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
            {[
              { value: 'replace', label: '🔄 Replace entire table', desc: 'Deletes all reviewer rows, then imports matching tune / ABC pages (or all if no filter).' },
              { value: 'upsert', label: '✏️ Merge (update + add)', desc: 'Updates rows that share page_id, inserts new tune pages not yet in the table.' },
              { value: 'add_new', label: '➕ Add new rows only', desc: 'Inserts only page_ids that are not already in the reviewer table.' },
            ].map(({ value, label, desc }) => (
              <label key={value} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 13 }}>
                <input
                  type="radio"
                  name="syncPullMode"
                  value={value}
                  checked={syncPullMode === value}
                  onChange={() => setSyncPullMode(value)}
                  style={{ marginTop: 2 }}
                />
                <span><span style={{ fontWeight: 600 }}>{label}</span>{' '}
                  <span style={{ color: 'var(--color-text-secondary)' }}>{desc}</span></span>
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn btn-primary" onClick={pullFromContentPages}>Run load</button>
            <button type="button" className="btn btn-secondary" onClick={() => setShowSyncPullPicker(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── Sync: push to content_pages ── */}
      {showSyncPushPicker && !syncWorking && (
        <div style={{
          border: '1px solid var(--color-border)',
          borderRadius: 12, padding: '16px 20px', marginBottom: 14,
          background: 'var(--color-surface)',
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>
            Apply this table to live tune pages
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
            {[
              { value: 'overwrite', label: '🔁 Overwrite', desc: 'Updates existing tune / ABC pages from staging. If staging page_id does not exist yet, creates a new tune page (e.g. after changing page_id). Empty cells can clear optional fields on updates.' },
              { value: 'add_new', label: '📝 Add only (fill empty)', desc: 'For existing pages: only fills empty fields. For staging page_ids with no live row yet: creates a new tune page with the same rules as fill-empty on an empty page.' },
              { value: 'create_missing', label: '➕ Create missing only', desc: 'INSERT new content_pages rows for page_ids in staging that do not exist yet. Skips rows that already have a tune or ABC page. HTML is a minimal placeholder; TunePage renders from metadata.' },
            ].map(({ value, label, desc }) => (
              <label key={value} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 13 }}>
                <input
                  type="radio"
                  name="syncPushMode"
                  value={value}
                  checked={syncPushMode === value}
                  onChange={() => setSyncPushMode(value)}
                  style={{ marginTop: 2 }}
                />
                <span><span style={{ fontWeight: 600 }}>{label}</span>{' '}
                  <span style={{ color: 'var(--color-text-secondary)' }}>{desc}</span></span>
              </label>
            ))}
          </div>

          {rows.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Staging table is empty — nothing to apply.</p>
          ) : (
            <div style={{
              marginBottom: 16, padding: '12px 14px',
              border: '1px dashed var(--color-border)',
              borderRadius: 10,
              background: 'var(--color-surface)',
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, color: 'var(--color-text-secondary)' }}>
                Preview (dry run — no writes yet)
              </div>
              {applyPushPreviewLoading && (
                <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-secondary)' }}>Computing preview…</p>
              )}
              {!applyPushPreviewLoading && applyPushPreview?.error && (
                <p style={{ margin: 0, fontSize: 13, color: 'var(--color-error)' }}>{applyPushPreview.error}</p>
              )}
              {!applyPushPreviewLoading && applyPushPreview && !applyPushPreview.error && applyPushPreview.dryRun && (
                <ToContentPagesApplyBreakdown payload={applyPushPreview} variant="preview" />
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-primary" onClick={pushToContentPages}>Run apply</button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setShowSyncPushPicker(false);
                setApplyPushPreview(null);
                setApplyPushPreviewLoading(false);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
        </div>
      ) : (
      <>
      {/* ── Compact header + tabs ── */}
      <div style={S.pageHeader}>
        <h1 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 700, lineHeight: 1.2, flex: '1 1 140px', minWidth: 0 }}>
          Tune metadata
        </h1>
        <div style={S.tabRow} role="tablist" aria-label="Reviewer sections">
          <button
            type="button"
            role="tab"
            className="admin-tune-metadata-tab"
            aria-selected={reviewerTab === 'review'}
            onClick={() => setReviewerTab('review')}
          >
            Review
          </button>
          <button
            type="button"
            role="tab"
            className="admin-tune-metadata-tab"
            aria-selected={reviewerTab === 'tools'}
            onClick={() => setReviewerTab('tools')}
          >
            Import &amp; tools
          </button>
        </div>
        <div style={S.headerActions}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 13,
              cursor: 'pointer',
              userSelect: 'none',
              whiteSpace: 'nowrap',
            }}
            title="Show or hide product covers and asset previews under ID columns"
          >
            <input
              type="checkbox"
              checked={showThumbnails}
              onChange={(e) => setShowThumbnails(e.target.checked)}
            />
            Thumbnails
          </label>
          {saveStatus ? (
            <span
              style={{
                fontSize: 12,
                padding: '4px 10px',
                borderRadius: 6,
                fontWeight: 600,
                background:
                  saveStatus === 'saved'
                    ? 'rgba(16,185,129,0.12)'
                    : saveStatus === 'saving'
                      ? 'rgba(79,70,229,0.1)'
                      : 'rgba(220,38,38,0.1)',
                color:
                  saveStatus === 'saved' ? 'var(--color-success)' : saveStatus === 'saving' ? 'var(--color-primary)' : 'var(--color-error)',
                border: `1px solid ${
                  saveStatus === 'saved'
                    ? 'color-mix(in srgb, var(--color-success) 45%, var(--color-border))'
                    : saveStatus === 'saving'
                      ? 'color-mix(in srgb, var(--color-primary) 45%, var(--color-border))'
                      : 'color-mix(in srgb, var(--color-error) 45%, var(--color-border))'
                }`,
              }}
            >
              {saveStatus === 'saving' ? 'Saving…' : saveStatus === 'saved' ? 'Saved' : 'Save error'}
            </span>
          ) : null}
          <button
            type="button"
            className="btn btn-secondary"
            style={{ fontSize: 13 }}
            onClick={() => loadFromApi()}
            disabled={loading}
            title="Reload staging rows from the server"
          >
            ↻ Refresh
          </button>
          <button
            className="btn btn-secondary"
            type="button"
            onClick={exportCSV}
            disabled={!sorted.length}
            style={{ fontSize: 13 }}
            title={sorted.length < rows.length
              ? `Export ${sorted.length} filtered tune(s) (search/filters applied)`
              : `Export all ${sorted.length} tune(s)`}
          >
            Export CSV{sorted.length < rows.length ? ` (${sorted.length})` : ''}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={uploading}
            style={{ cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.7 : 1, fontSize: 13 }}
            onClick={() => setShowUploadPicker((p) => !p)}
          >
            {uploading ? 'Uploading…' : 'Upload CSV ▾'}
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={onUpload}
          disabled={uploading}
          style={{ display: 'none' }}
        />
      </div>

      {/* ── Upload mode picker ── */}
      {showUploadPicker && !uploading && (
        <div style={{
          border: '1px solid var(--color-border)',
          borderRadius: 12, padding: '16px 20px', marginBottom: 14,
          background: 'var(--color-surface)',
          display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end',
        }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, color: 'var(--color-text-primary)' }}>
              Choose upload mode
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                { value: 'replace',  label: '🔄 Replace all',          desc: 'Clears the table then imports the entire CSV' },
                { value: 'upsert',   label: '✏️ Update & add new',      desc: 'Updates existing records, inserts new ones' },
                { value: 'add_new',  label: '➕ Add new only',          desc: 'Skips records that already exist (by page_id)' },
              ].map(({ value, label, desc }) => (
                <label key={value} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 13 }}>
                  <input
                    type="radio"
                    name="uploadMode"
                    value={value}
                    checked={uploadMode === value}
                    onChange={() => setUploadMode(value)}
                    style={{ marginTop: 2 }}
                  />
                  <span>
                    <span style={{ fontWeight: 600 }}>{label}</span>
                    <span style={{ color: 'var(--color-text-secondary)', marginLeft: 8 }}>{desc}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn btn-primary"
              onClick={() => fileInputRef.current?.click()}
              style={{ whiteSpace: 'nowrap' }}
            >
              Select File…
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => setShowUploadPicker(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}


      {/* ── Upload result summary ── */}
      {uploadResult && (
        <div style={{
          padding: '10px 16px', borderRadius: 10, marginBottom: 14,
          background: 'rgba(16,185,129,0.07)', border: '1px solid #6ee7b7',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontSize: 13, flexWrap: 'wrap', gap: 8,
        }}>
          <span>
            <strong>✓ Upload complete</strong>
            {' · '}
            <span style={{ color: 'var(--color-text-secondary)' }}>
              Mode: <strong>
                {uploadResult.mode === 'replace' ? 'Replace all'
                  : uploadResult.mode === 'upsert' ? 'Update & add new'
                  : 'Add new only'}
              </strong>
              {' · '}
              {uploadResult.attempted} row{uploadResult.attempted !== 1 ? 's' : ''} processed
              {uploadResult.noPageId > 0 && ` · ${uploadResult.noPageId} skipped (no page_id)`}
              {' · '}
              <strong>{uploadResult.totalInTable}</strong> total in table
              {' · '}
              Search and filters were reset so the table shows all rows.
            </span>
          </span>
          <button className="admin-tune-metadata-icon-btn" onClick={() => setUploadResult(null)} style={{ fontSize: 16, color: 'var(--color-success)' }}>✕</button>
        </div>
      )}

      {/* ── Research in progress ── */}
      {repairStatus && (
        <div style={S.banner('rgba(79,70,229,0.08)', '#a5b4fc', 'var(--color-text-primary)')}>
          <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span>
          <span>{repairStatus}</span>
        </div>
      )}

      {/* ── Research Review Panel ── */}
      {Object.keys(pendingResearch).length > 0 && !repairing && (() => {
        const entries = Object.entries(pendingResearch);
        const withChanges = entries.filter(([, p]) => RESEARCH_FIELDS.some(f => p.proposed[f] !== undefined));
        const failed = entries.filter(([, p]) => p.error);
        return (
          <div className="admin-tune-metadata-research-panel">
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
              <strong style={{ fontSize: 15, color: 'var(--color-success)' }}>
                🔬 Research Results — {withChanges.length} tune{withChanges.length !== 1 ? 's' : ''} with new data (review before applying)
              </strong>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={applyPending}
                >
                  ✅ Apply All
                </button>
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={discardPending}
                >
                  Discard
                </button>
              </div>
            </div>

            {/* Per-tune cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {entries.map(([id, p]) => {
                const hasChanges = RESEARCH_FIELDS.some(f => p.proposed[f] !== undefined);
                return (
                  <div key={id} style={{
                    border: '1px solid var(--color-border)',
                    borderRadius: 8, padding: '10px 14px',
                    background: p.error ? 'rgba(220,38,38,0.04)' : hasChanges ? 'rgba(5,150,105,0.04)' : 'rgba(0,0,0,0.02)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: hasChanges ? 8 : 0 }}>
                      <strong style={{ fontSize: 13 }}>{p.title}</strong>
                      {p.error
                        ? <span style={{ fontSize: 12, color: 'var(--color-error)' }}>⚠ failed: {p.error}</span>
                        : !hasChanges
                          ? <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>no new data found</span>
                          : <span style={{ fontSize: 12, color: 'var(--color-success)' }}>via {p.sources.join(', ')}</span>
                      }
                    </div>
                    {hasChanges && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                        {RESEARCH_FIELDS.filter(f => p.proposed[f] !== undefined).map(f => (
                          <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 12, color: 'var(--color-text-secondary)', minWidth: 90 }}>{f.replace(/_/g, ' ')}:</span>
                            <span style={{ fontSize: 12, color: '#9ca3af', textDecoration: 'line-through', marginRight: 2 }}>
                              {p.original[f] && !isMissing(p.original[f]) ? p.original[f] : '∅'}
                            </span>
                            <span style={{ fontSize: 12, marginRight: 4 }}>→</span>
                            <input
                              value={p.proposed[f]}
                              onChange={e => updatePending(id, f, e.target.value)}
                              style={{ ...S.input, padding: '3px 8px', fontSize: 12, minWidth: 140, width: 'auto', borderColor: 'var(--color-success)' }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Quick links from research */}
                    {Object.entries(LINK_MAP).some(([, storeKey]) => p.proposed[storeKey]) && (
                      <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {Object.entries(LINK_MAP).map(([k, storeKey]) => p.proposed[storeKey] ? (
                          <a key={k} href={p.proposed[storeKey]} target="_blank" rel="noreferrer"
                            style={{ fontSize: 11, color: 'var(--color-accent)', textDecoration: 'underline' }}>
                            {k.replace(/_/g, ' ')} ↗
                          </a>
                        ) : null)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {failed.length > 0 && (
              <div style={{ marginTop: 10, fontSize: 12, color: 'var(--color-error)' }}>
                ⚠ {failed.length} tune{failed.length !== 1 ? 's' : ''} failed (network/timeout)
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Auto-tag result banner ── */}
      {autoTagResult && !showAutoTagPanel && (
        <div style={{
          ...S.banner('rgba(124,58,237,0.07)', '#c4b5fd', 'var(--color-text-primary)'),
          justifyContent: 'space-between',
        }}>
          <span>
            <strong>🏷 Auto-tag complete</strong>
            {' · '}
            <strong>{autoTagResult.updated}</strong> rows updated
            {autoTagResult.skipped > 0 && ` · ${autoTagResult.skipped} skipped (no change)`}
            {' out of '}
            {autoTagResult.total} target rows
          </span>
          <button onClick={() => setAutoTagResult(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>
      )}

      {/* ── Auto-tag progress ── */}
      {autoTagProgress && (
        <div style={S.banner('rgba(124,58,237,0.07)', '#c4b5fd', '#7c3aed')}>
          <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span>
          <span>{autoTagProgress}</span>
        </div>
      )}

      {reviewerTab === 'review' && (
      <>
      {/* ── Summary strip (compact) ── */}
      {stats && (
        <div style={S.statInline}>
          {STAT_ITEMS.map(({ label, value, accent }) => (
            <span key={label}>
              <strong style={{ color: accent, fontSize: 15 }}>{value}</strong>
              <span style={{ color: 'var(--color-text-secondary)', marginLeft: 6 }}>{label}</span>
            </span>
          ))}
        </div>
      )}

      {rows.length > 0 && filtered.length === 0 && (
        <div className="admin-tune-metadata-warning-banner">
          <span>
            <strong>{rows.length}</strong> row{rows.length !== 1 ? 's are' : ' is'} in staging but{' '}
            <strong>hidden</strong> by search or filters above (e.g. &quot;Core missing only&quot; or region/key).
          </span>
          <button type="button" className="btn btn-secondary" onClick={resetReviewerViewFilters}>
            Clear search &amp; filters
          </button>
        </div>
      )}

      {/* ── Filter bar ── */}
      {rows.length > 0 && (
        <div style={S.filterBar}>
          <div>
            <div style={S.filterLabel}>Search</div>
            <input
              style={{ ...S.input, minWidth: 260 }}
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div>
            <div style={S.filterLabel}>Region</div>
            <select style={S.select} value={filters.region}
              onChange={(e) => setFilters(f => ({ ...f, region: e.target.value }))}>
              <option value="All">All Regions</option>
              {uniqueValues.regions.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>

          <div>
            <div style={S.filterLabel}>Type</div>
            <select style={S.select} value={filters.tuneType}
              onChange={(e) => setFilters(f => ({ ...f, tuneType: e.target.value }))}>
              <option value="All">All Types</option>
              {uniqueValues.types.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>

          <div>
            <div style={S.filterLabel}>Key</div>
            <select style={S.select} value={filters.key}
              onChange={(e) => setFilters(f => ({ ...f, key: e.target.value }))}>
              <option value="All">All Keys</option>
              {uniqueValues.keys.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input type="checkbox" checked={filters.missingOnly}
              onChange={(e) => setFilters(f => ({ ...f, missingOnly: e.target.checked }))} />
            Core missing only
          </label>

          <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--color-text-secondary)', paddingBottom: 2, alignSelf: 'flex-end' }}>
            Matching {sorted.length} / {rows.length} total
            {frozenMissingRowIds && (
              <span style={{ display: 'block', fontSize: 11, marginTop: 2, color: 'var(--color-primary)' }}>
                Working set locked ({frozenMissingRowIds.size} tune{frozenMissingRowIds.size !== 1 ? 's' : ''}) — change missing-field filters to refresh
              </span>
            )}
          </span>

          <details style={{ width: '100%', marginTop: 4 }}>
            <summary style={{ cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)', userSelect: 'none' }}>
              Missing field filters (per-column)
            </summary>
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed var(--color-border)' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center' }}>
                {[
                  ['composer', 'Missing Composer'],
                  ['tune_type', 'Missing Type'],
                  ['key_signature', 'Missing Key'],
                  ['region', 'Missing Region'],
                  ['time_signature', 'Missing Time'],
                ].map(([key, label]) => (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                    <input type="checkbox" checked={missingFilters[key]}
                      onChange={(e) => setMissingFilters(prev => ({ ...prev, [key]: e.target.checked }))} />
                    {label}
                  </label>
                ))}
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                  <input type="checkbox" checked={missingFilters.custom}
                    onChange={(e) => setMissingFilters(prev => ({ ...prev, custom: e.target.checked }))} />
                  Missing
                </label>
                <select style={S.select} value={customMissingField}
                  onChange={(e) => setCustomMissingField(e.target.value)}>
                  {headers.map((h) => (
                    <option key={h} value={h}>{COLUMN_DISPLAY_LABELS[h] || h}</option>
                  ))}
                </select>
              </div>
            </div>
          </details>
        </div>
      )}

      {/* ── Table pagination (sorted/filtered view) ── */}
      {rows.length > 0 && sorted.length > 0 && (
        <div className="admin-tune-metadata-panel" style={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px 16px',
          marginBottom: 10, padding: '10px 12px',
          border: '1px solid var(--color-border)',
          borderRadius: 10,
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <span style={{ color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>Rows per page</span>
            <select
              style={S.select}
              value={tablePageSize}
              onChange={(e) => {
                setTablePageSize(Number(e.target.value));
                setTablePage(1);
              }}
            >
              {TABLE_PAGE_SIZES.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
          <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
            Showing <strong>{tablePageOffset + 1}</strong>
            {'–'}
            <strong>{Math.min(tablePageOffset + paginatedSorted.length, sorted.length)}</strong>
            {' of '}
            <strong>{sorted.length}</strong> matching
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ fontSize: 13, padding: '6px 12px' }}
              disabled={tablePage <= 1}
              onClick={() => setTablePage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span style={{ fontSize: 13, minWidth: 120, textAlign: 'center' }}>
              Page <strong>{tablePage}</strong> of <strong>{tableTotalPages}</strong>
            </span>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ fontSize: 13, padding: '6px 12px' }}
              disabled={tablePage >= tableTotalPages}
              onClick={() => setTablePage((p) => Math.min(tableTotalPages, p + 1))}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* ── Table ── */}
      {rows.length > 0 ? (
        <div style={S.tableWrapper}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={{ ...S.th('_num'), minWidth: 42, width: 42, textAlign: 'right', cursor: 'default', paddingRight: 8 }}>#</th>
                <th style={{ ...S.th('_expand'), minWidth: 32, width: 32 }} />
                {headers.map(h => {
                  const labelLines = splitHeaderLabel(h);
                  return (
                    <th key={h} style={{ ...S.th(h), position: 'relative' }} onClick={() => requestSort(h)}>
                      <div>{labelLines.map((line, idx) => <div key={`${h}-${idx}`}>{line}</div>)}</div>
                      <div style={{ fontSize: 11, marginTop: 2 }}>
                        {sortConfig.key === h ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                      </div>
                      <button
                        type="button"
                        title={`Remove column "${h}"`}
                        onClick={(e) => { e.stopPropagation(); deleteColumn(h); }}
                        style={{
                          position: 'absolute', top: 2, right: 2,
                          background: 'none', border: 'none', cursor: 'pointer',
                          fontSize: 10, lineHeight: 1, padding: '1px 3px',
                          color: 'var(--color-text-secondary)',
                          borderRadius: 3, opacity: 0.5,
                        }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.color = 'var(--color-error)'; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = '0.5'; e.currentTarget.style.color = ''; }}
                      >✕</button>
                    </th>
                  );
                })}
                <th style={{ ...S.th('_delete'), minWidth: 64, width: 64, cursor: 'default' }}>Del</th>
              </tr>
            </thead>
            <tbody>
              {paginatedSorted.map((r, rowIdx) => (
                <React.Fragment key={r._id}>
                  <tr style={{ background: (isMissing(r.composer) || isMissing(r.tune_type) || isMissing(r.key_signature)) ? 'rgba(251,191,36,0.04)' : 'transparent' }}>
                    <td style={{ ...S.td, textAlign: 'right', paddingRight: 10, fontSize: 12,
                      color: 'var(--color-text-secondary)', userSelect: 'none',
                      whiteSpace: 'nowrap', minWidth: 42 }}>
                      {tablePageOffset + rowIdx + 1}
                    </td>
                    <td style={{ ...S.td, textAlign: 'center', cursor: 'pointer' }}
                      onClick={() => setExpandedId(expandedId === r._id ? null : r._id)}>
                      <span style={{ fontSize: 16 }}>{expandedId === r._id ? '▲' : '▼'}</span>
                    </td>

                    {headers.map(h => (
                      <td key={h} style={S.td}>
                        {h === 'updated_at' ? (
                          <span style={{
                            fontSize: 12, color: 'var(--color-text-secondary)',
                            whiteSpace: 'nowrap', display: 'block', padding: '5px 2px'
                          }}>
                            {fmtDate(r[h])}
                          </span>
                        ) : h === 'tags' ? (
                          <TagEditor
                            compact
                            value={r[h] || ''}
                            onSave={(val) => {
                              updateCell(r._id, 'tags', val);
                              saveRow(r.page_id, 'tags', val);
                            }}
                          />
                        ) : DROPDOWN_FIELDS.has(h) ? (
                          <TuneFieldSelectWithAdd
                            value={h === 'time_signature' ? (normalizeTimeSignature(r[h]) || '') : (r[h] || '')}
                            options={fieldAllOptions[h] || []}
                            fieldLabel={h.replace(/_/g, ' ')}
                            highlightEmpty
                            allowAddNew={h !== 'time_signature'}
                            onSave={(val) => {
                              const nextVal = h === 'time_signature' ? normalizeTimeSignature(val) : val;
                              updateCell(r._id, h, nextVal);
                              saveRow(r.page_id, h, nextVal);
                            }}
                          />
                        ) : h === 'product_names' ? (
                          <div>
                            <input
                              value={r[h] || ''}
                              onChange={(e) => updateCell(r._id, h, e.target.value)}
                              onBlur={(e) => onCellBlur(r.page_id, h, e.target.value)}
                              style={S.cell(h, r[h])}
                              placeholder="Comma-separated product IDs (e.g. 624, 626)"
                            />
                            {(() => {
                              const ids = parseProductIdsFromCsv(r[h]);
                              if (ids.length === 0) return null;
                              return (
                                <div
                                  style={{
                                    marginTop: 6,
                                    fontSize: 11,
                                    lineHeight: 1.45,
                                    color: 'var(--color-text-secondary)',
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '6px 14px',
                                  }}
                                >
                                  {ids.map((id) => {
                                    const name = productCatalog?.get(String(id))?.name;
                                    const loading = productCatalog === null;
                                    const hasName = Boolean(name);
                                    return (
                                      <span key={id} style={{ whiteSpace: 'nowrap' }}>
                                        {loading ? (
                                          <>
                                            <span style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>#{id}</span>
                                            <span style={{ marginLeft: 5, opacity: 0.5 }}>…</span>
                                          </>
                                        ) : hasName ? (
                                          <>
                                            <span
                                              style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}
                                              title={`Product #${id}`}
                                            >
                                              {name}
                                            </span>
                                            <span style={{ marginLeft: 6, opacity: 0.5, fontSize: 10 }}>#{id}</span>
                                          </>
                                        ) : (
                                          <>
                                            <span style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>#{id}</span>
                                            <span style={{ marginLeft: 5, opacity: 0.45 }}>—</span>
                                          </>
                                        )}
                                      </span>
                                    );
                                  })}
                                </div>
                              );
                            })()}
                            {showThumbnails && (
                              <TuneProductThumbnails idsCsv={r[h]} catalog={productCatalog} />
                            )}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openProductSearchForRow(r);
                              }}
                              style={{
                                display: 'block',
                                marginTop: 4,
                                padding: 0,
                                border: 'none',
                                background: 'none',
                                cursor: 'pointer',
                                fontSize: 11,
                                color: 'var(--color-primary)',
                                textDecoration: 'underline',
                                textAlign: 'left',
                              }}
                              title="Browse shop products and append the chosen numeric product ID"
                            >
                              Browse products…
                            </button>
                          </div>
                        ) : h === 'notation_asset_ids' || h === 'audio_asset_ids' ? (
                          <div>
                            <input
                              value={r[h] || ''}
                              onChange={(e) => updateCell(r._id, h, e.target.value)}
                              onBlur={(e) => onCellBlur(r.page_id, h, e.target.value)}
                              style={S.cell(h, r[h])}
                            />
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openAssetSearchForField(r, h);
                              }}
                              style={{
                                display: 'block',
                                marginTop: 4,
                                padding: 0,
                                border: 'none',
                                background: 'none',
                                cursor: 'pointer',
                                fontSize: 11,
                                color: 'var(--color-primary)',
                                textDecoration: 'underline',
                                textAlign: 'left',
                              }}
                              title={
                                h === 'notation_asset_ids'
                                  ? 'Search image assets and append the chosen ID'
                                  : 'Search audio assets and append the chosen ID'
                              }
                            >
                              Search & add asset…
                            </button>
                            {showThumbnails &&
                              (h === 'notation_asset_ids' ? (
                                <TuneNotationThumbnails idsCsv={r.notation_asset_ids} />
                              ) : (
                                <TuneAudioThumbnails idsCsv={r.audio_asset_ids} />
                              ))}
                          </div>
                        ) : (
                          <>
                            <input
                              value={r[h] || ''}
                              onChange={(e) => updateCell(r._id, h, e.target.value)}
                              onBlur={(e) => onCellBlur(r.page_id, h, e.target.value)}
                              style={S.cell(h, r[h])}
                            />
                            {URL_COLS.has(h) && !!r[h] && (
                              <a href={r[h]} target="_blank" rel="noreferrer" style={S.smallLink}>open ↗</a>
                            )}
                          </>
                        )}
                      </td>
                    ))}

                    <td style={S.td}>
                      <button type="button" style={S.deleteBtn} onClick={() => deleteRow(r._id)}>✕</button>
                    </td>
                  </tr>

                  {expandedId === r._id && (
                    <tr>
                      <td colSpan={headers.length + 3} style={S.expandedPanel}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 18 }}>
                          <div>
                            <strong style={{ fontSize: 13 }}>Description</strong>
                            <p style={{ fontSize: 13, marginTop: 6, fontStyle: 'italic',
                              borderLeft: '3px solid var(--color-primary)',
                              paddingLeft: 10, color: 'inherit' }}>
                              {r.description || 'No description available.'}
                            </p>
                          </div>
                          <div>
                            <strong style={{ fontSize: 13 }}>Quick links</strong>
                            <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {[
                                { label: 'Tune page',           val: r.tune_page_url },
                                { label: 'Source',              val: r.source_url },
                                { label: 'CBFiddle search',     val: r.cbfiddle_search_url     || r._cbfiddle_search_url },
                                { label: 'CBFiddle match',      val: r.cbfiddle_match_url      || r._cbfiddle_match_url },
                                { label: 'TheSession search',   val: r.thesession_search_url   || r._thesession_search_url },
                                { label: 'TheSession match',    val: r.thesession_match_url    || r._thesession_match_url },
                                { label: 'ABCNotation search',  val: r.abcnotation_search_url  || r._abcnotation_search_url },
                                { label: 'Google CSE search',   val: r.google_cse_search_url   || r._google_cse_search_url },
                              ].filter(lv => lv.val).map(({ label, val }) => (
                                <a key={label} href={val} target="_blank" rel="noreferrer" style={S.link}>
                                  {label} →
                                </a>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* ── Tags editor (full palette) ── */}
                        <div style={{
                          borderTop: '1px dashed var(--color-border)',
                          paddingTop: 14,
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                            <strong style={{ fontSize: 13 }}>Tags</strong>
                            <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                              Click ⊕ to browse palette · type + Enter to add custom · × to remove
                            </span>
                          </div>
                          <TagEditor
                            value={r.tags || ''}
                            onSave={(val) => {
                              updateCell(r._id, 'tags', val);
                              saveRow(r.page_id, 'tags', val);
                            }}
                          />
                          {/* Tag category legend */}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 10 }}>
                            {Object.entries(TAG_CAT_COLORS).map(([cat, color]) => (
                              <span key={cat} style={{
                                fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                                color, letterSpacing: '.04em',
                                display: 'flex', alignItems: 'center', gap: 4,
                              }}>
                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} />
                                {cat}
                              </span>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={S.empty}>
          {loading ? (
            <>
              <div style={{ fontSize: 36, marginBottom: 12 }}>⏳</div>
              <h3>Loading from database…</h3>
            </>
          ) : rowsLoadError ? (
            <>
              <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
              <h3>Could not load staging rows</h3>
              <p style={{ marginBottom: 12, maxWidth: 520, margin: '0 auto 12px', color: 'var(--color-text-primary)' }}>
                {rowsLoadError}
              </p>
              <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 16 }}>
                After fixing the issue, restart the backend if you recently changed the database file, then retry.
              </p>
              <button
                type="button"
                className="btn btn-primary"
                style={{ marginRight: 10 }}
                onClick={() => loadFromApi()}
              >
                Retry load
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setRowsLoadError('')}
              >
                Dismiss
              </button>
            </>
          ) : (
            <>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🎵</div>
              <h3>No data in staging table</h3>
              <p style={{ marginBottom: 6 }}>Upload a CSV to import tunes into the database.</p>
              <p style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                Use <code>pipeline_07_final_clean.csv</code> from the pipeline output folder.
              </p>
              <button
                className="btn btn-primary"
                disabled={uploading}
                style={{ cursor: uploading ? 'not-allowed' : 'pointer', marginTop: 16 }}
                onClick={() => setShowUploadPicker(p => !p)}
              >
                {uploading ? '⏳ Uploading…' : '⬆ Upload CSV ▾'}
              </button>
            </>
          )}
        </div>
      )}
      </>
      )}

      {reviewerTab === 'tools' && (
        <div style={{ maxWidth: 960, marginBottom: 18 }}>
          <div style={S.toolsSection}>
            <h3 style={S.toolsSectionTitle}>Dangerous zone</h3>
            <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: '0 0 12px' }}>
              Pull from or push to live tune pages. Confirm each action in the overlay.
            </p>
            <button type="button" className="btn btn-secondary" onClick={() => setShowDangerousZone(true)}>
              Open dangerous zone…
            </button>
          </div>

          <div style={S.toolsSection}>
            <h3 style={S.toolsSectionTitle}>Web research (batch)</h3>
            <p style={{ fontSize: 13, margin: '0 0 12px', color: 'var(--color-text-primary)' }}>
              <strong>{incompleteInView}</strong> row{incompleteInView !== 1 ? 's' : ''} in the current filter missing composer, type, or key.
              The next run processes up to <strong>{batchTarget}</strong>.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
              <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                Batch size
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={batchSize}
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10);
                    setBatchSize(Number.isFinite(n) ? Math.max(1, Math.min(50, n)) : 1);
                  }}
                  style={{ width: 72, ...S.input }}
                />
              </label>
              <button
                type="button"
                className="btn btn-primary"
                onClick={researchBatch}
                disabled={repairing || !rows.length || incompleteInView === 0}
              >
                {repairing ? 'Researching…' : 'Run research on batch'}
              </button>
            </div>
          </div>

          <div style={S.toolsSection}>
            <h3 style={S.toolsSectionTitle}>Auto-tag from fields</h3>
            <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: '0 0 12px' }}>
              Derive comma-separated tags from staging columns (and product titles when enabled). Results appear above when complete.
            </p>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowAutoTagPanel((v) => !v)}
            >
              {showAutoTagPanel ? 'Hide auto-tag options' : 'Configure & run auto-tag…'}
            </button>
            {showAutoTagPanel && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px dashed var(--color-border)' }}>
                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10, color: 'var(--color-text-secondary)' }}>
                  Include tag sources
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                  {AUTO_TAG_SOURCES.map(({ key, label, example, always }) => (
                    <label
                      key={key}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 10,
                        fontSize: 13,
                        cursor: always ? 'default' : 'pointer',
                        opacity: always ? 0.95 : 1,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={autoTagSources.has(key)}
                        disabled={!!always}
                        onChange={() => {
                          if (always) return;
                          setAutoTagSources((prev) => {
                            const n = new Set(prev);
                            if (n.has(key)) n.delete(key);
                            else n.add(key);
                            return n;
                          });
                        }}
                        style={{ marginTop: 2 }}
                      />
                      <span>
                        <strong>{label}</strong>
                        <span style={{ color: 'var(--color-text-secondary)', marginLeft: 6 }}>{example}</span>
                      </span>
                    </label>
                  ))}
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, color: 'var(--color-text-secondary)' }}>
                  Target rows
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
                  {[
                    { value: 'filtered', label: 'Current filter', desc: 'Respects search and filters' },
                    { value: 'all', label: 'All rows', desc: 'Every row in staging' },
                    { value: 'empty_only', label: 'Empty tags only', desc: 'Rows with blank tags' },
                  ].map(({ value, label, desc }) => (
                    <label key={value} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="autoTagMode"
                        value={value}
                        checked={autoTagMode === value}
                        onChange={() => setAutoTagMode(value)}
                        style={{ marginTop: 2 }}
                      />
                      <span>
                        <span style={{ fontWeight: 600 }}>{label}</span>
                        <span style={{ color: 'var(--color-text-secondary)', marginLeft: 6 }}>{desc}</span>
                      </span>
                    </label>
                  ))}
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 14, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={autoTagMerge}
                    onChange={(e) => setAutoTagMerge(e.target.checked)}
                  />
                  Merge with existing tags (off = replace tags entirely)
                </label>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => runAutoTagging()}
                  disabled={!!autoTagProgress || !rows.length}
                >
                  {autoTagProgress ? 'Running…' : 'Run auto-tag'}
                </button>
              </div>
            )}
          </div>

          <div style={S.toolsSection}>
            <h3 style={S.toolsSectionTitle}>Table schema</h3>
            {!addingCol ? (
              <button type="button" className="btn btn-secondary" onClick={() => setAddingCol(true)}>
                Add column…
              </button>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                <input
                  style={{ ...S.input, minWidth: 200 }}
                  placeholder="column_name"
                  value={newColName}
                  onChange={(e) => setNewColName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addColumn()}
                />
                <button type="button" className="btn btn-primary" onClick={addColumn}>
                  Add column
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setAddingCol(false);
                    setNewColName('');
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
            <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: addingCol ? '12px 0 0' : '12px 0 0' }}>
              Client CSV exports every row in memory. Use <strong>Export CSV</strong> in the header to export the current search/filter view.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 10 }}>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={!rows.length}
                onClick={() => {
                  const csv = toCSV(headers, rows);
                  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = 'tune_metadata_client.csv';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                }}
              >
                Download CSV (client)
              </button>
              <button type="button" className="btn btn-danger" disabled={!rows.length} onClick={clearTable}>
                Clear entire staging table
              </button>
            </div>
          </div>
        </div>
      )}

      </>
      )}

      <AssetSearchModal
        key={assetSearchModal ? `${assetSearchModal.rowId}-${assetSearchModal.field}` : 'closed'}
        isOpen={!!assetSearchModal}
        onClose={() => setAssetSearchModal(null)}
        onSelectAsset={handleAssetSearchSelect}
        fileType={assetSearchModal?.fileType ?? null}
      />

      <ProductSearchModal
        key={productSearchModal ? `prod-${productSearchModal.rowId}` : 'prod-closed'}
        isOpen={!!productSearchModal}
        onClose={() => setProductSearchModal(null)}
        onSelectProduct={handleProductSearchSelect}
      />
    </div>
  );
}
